import { useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FAQS as faqs } from "@/data/faqs";
import CompetitorCompareDialog from "./CompetitorCompareDialog";

// The FAQ answer for this question is the plain-text summary plus a link
// that pops open the full comparison table (in CompetitorCompareDialog).
// Matched by exact question string so the FAQ registry stays
// JSON-serialisable (used by FAQPage JSON-LD).
const COMPETITOR_FAQ_Q = "How does Cello compare to traditional styling and Stitch Fix?";

interface FAQSectionBassProps {
    /** Splash pages: below md, render the mobile card list; adds the #faq wrapper. */
    mobileRedesign?: boolean;
}

const FAQSectionBass = ({ mobileRedesign = false }: FAQSectionBassProps = {}) => {
    const [openItems, setOpenItems] = useState<number[]>([0]);
    const [compareOpen, setCompareOpen] = useState(false);

    const toggleItem = (index: number) => {
        setOpenItems(prev =>
            prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
        );
    };

    // Mobile: centered kicker + heading, then a white card list. Multi-open;
    // the first row starts open. Answers carry the orange left rule.
    const mobile = (
        <section className="md:hidden bg-[#F9FAFB] px-6 py-[72px]">
            <p className="mb-3 text-center text-[11px] font-semibold uppercase tracking-[0.25em] text-[#FF8B11]">Got questions?</p>
            <h2 className="mb-7 text-center font-playfair text-[34px] font-bold leading-[1.1] text-[#0f172a]">
                Frequently Asked Questions
            </h2>
            <div className="overflow-hidden rounded-2xl bg-white shadow-[0_1px_0_rgba(15,23,42,0.06),0_16px_40px_-24px_rgba(15,23,42,0.3)]">
                {faqs.map((faq, index) => {
                    const isOpen = openItems.includes(index);
                    const panelId = `faq-m-panel-${index}`;
                    return (
                        <div key={index} className="border-t border-[#f1ede7] first:border-t-0">
                            <button
                                type="button"
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                                onClick={() => toggleItem(index)}
                                className="flex min-h-[60px] w-full items-center justify-between gap-4 px-5 py-[18px] text-left"
                            >
                                <span className="font-playfair text-[16px] font-bold leading-[1.4] text-[#0f172a]">{faq.question}</span>
                                <span aria-hidden="true" className="shrink-0 font-playfair text-[22px] leading-none text-[#FF8B11]">
                                    {isOpen ? "−" : "+"}
                                </span>
                            </button>
                            {isOpen && (
                                <div id={panelId} className="mx-5 mb-5 border-l-2 border-[#c2410c] pl-3.5">
                                    {faq.question === COMPETITOR_FAQ_Q ? (
                                        <button
                                            type="button"
                                            onClick={() => setCompareOpen(true)}
                                            className="text-left text-[14px] font-semibold leading-[1.65] text-[#c2410c] underline underline-offset-4 decoration-[#c2410c]/40"
                                        >
                                            {faq.answer}
                                        </button>
                                    ) : (
                                        <p className="whitespace-pre-line text-[14px] leading-[1.65] text-[#334155]">{faq.answer}</p>
                                    )}
                                    {faq.link && (
                                        <Link
                                            to={faq.link.href}
                                            className="mt-4 inline-flex min-h-[44px] items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.15em] text-[#c2410c]"
                                        >
                                            {faq.link.label}
                                            <ArrowRight className="h-3.5 w-3.5" />
                                        </Link>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );

    const desktop = (
        <section id={mobileRedesign ? undefined : "faq"} className="relative bg-white overflow-hidden py-24 sm:py-32 border-t border-[#e2d8ce]">
            {/* Minimalist Architectural Background */}
            <div className="absolute inset-0 bg-white" />
            <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:24px_24px] opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-transparent to-white/60" />

            <div className="max-w-[800px] mx-auto relative z-10 px-4 sm:px-8">
                <div className="text-center mb-16 sm:mb-24 flex flex-col items-center">
                    <span className="text-[#c2410c] text-[10px] sm:text-[11px] font-bold tracking-[0.2em] uppercase mb-4">
                        Got Questions?
                    </span>
                    <h2 className="text-[32px] sm:text-[40px] md:text-[48px] lg:text-[52px] font-bold font-playfair leading-[1.05] tracking-tight text-[#0a0502]">
                        Frequently Asked Questions
                    </h2>
                </div>
                
                <div className="border-t-2 border-slate-900">
                    {faqs.map((faq, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.05 }}
                            className="border-b border-slate-200 hover:bg-slate-50 transition-colors"
                        >
                            <Collapsible open={openItems.includes(index)} onOpenChange={() => toggleItem(index)}>
                                <CollapsibleTrigger className="w-full py-8 px-4 sm:px-6 text-left group flex items-start justify-between gap-6 cursor-pointer">
                                    <h3 className="text-lg sm:text-xl font-bold font-playfair text-[#0a0502] tracking-tight group-hover:text-[#c2410c] transition-colors pr-2">
                                        {faq.question}
                                    </h3>
                                    <div className="flex-shrink-0 mt-2">
                                        <div className="relative w-4 h-4 flex items-center justify-center">
                                            <div className={`absolute w-full h-[2px] transition-all duration-300 ${openItems.includes(index) ? 'bg-[#c2410c] rotate-180' : 'bg-[#0a0502]'}`} />
                                            <div className={`absolute w-[2px] h-full transition-all duration-300 ${openItems.includes(index) ? 'bg-[#c2410c] rotate-90 opacity-0' : 'bg-[#0a0502]'}`} />
                                        </div>
                                    </div>
                                </CollapsibleTrigger>
                                <CollapsibleContent className="pb-8 px-4 sm:px-6">
                                    <div className="border-l-2 border-[#c2410c] pl-4 pr-6 md:pr-16">
                                        {faq.question === COMPETITOR_FAQ_Q ? (
                                            <button
                                                type="button"
                                                onClick={() => setCompareOpen(true)}
                                                className="text-left text-[14px] sm:text-[15px] font-semibold text-[#c2410c] underline underline-offset-4 decoration-[#c2410c]/40 hover:text-[#c2410c] hover:decoration-[#c2410c] transition-colors"
                                            >
                                                {faq.answer}
                                            </button>
                                        ) : (
                                            <p className="text-[14px] sm:text-[15px] text-slate-600 leading-relaxed whitespace-pre-line">
                                                {faq.answer}
                                            </p>
                                        )}
                                        {faq.link && (
                                            <Link
                                                to={faq.link.href}
                                                className="group/link mt-5 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.15em] text-[#c2410c] hover:text-[#c2410c] transition-colors"
                                            >
                                                {faq.link.label}
                                                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/link:translate-x-1" />
                                            </Link>
                                        )}
                                    </div>
                                </CollapsibleContent>
                            </Collapsible>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );

    const compareDialog = <CompetitorCompareDialog open={compareOpen} onOpenChange={setCompareOpen} />;

    if (!mobileRedesign) {
        return (
            <>
                {desktop}
                {compareDialog}
            </>
        );
    }

    // The anchor lives on the wrapper so the header's "FAQ" link works on both
    // trees and lands below the mobile sticky group.
    return (
        <>
            <div id="faq" className="scroll-mt-[var(--sticky-h,0px)] md:scroll-mt-0">
                {mobile}
                <div className="hidden md:block">{desktop}</div>
            </div>
            {compareDialog}
        </>
    );
};

export default FAQSectionBass;
